import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Skeleton,
  Space,
  Spin,
  Typography,
  message,
  Collapse,
  Tabs,
} from 'antd'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  DOWNLOAD_RESOURCE,
  GET_ASSIGNEE_USER_GROUPS,
  GET_TASK,
} from '../../../graphql/query/organization'
import './styles.css'
import { IAssigneeGroup, IDownloadResourceResp, ITask } from '../../OrganizationDetails/types'
import { DownloadOutlined, LeftOutlined } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import Header from '../../../components/Header/Header'
import { COMMON_CONSTANTS } from '../../../commonConstants'
import Status from '../../AOI/helperComponents/StatusModal/Status'
import { downloadResourceFromUri } from '../../../utils/resourceUtils'
import { ASSIGN_TASKS_TO_USER } from '../../../graphql/mutation/organization'
import { noneDropdownItem } from '../../OrganizationDetails/constants'
import { format } from 'date-fns-tz'

const SiteDetails = () => {
  const { Panel } = Collapse
  const navigate = useNavigate()
  const [isDownloading, setIsDownloading] = useState<boolean>(false)
  const [isAssigning, setIsAssigning] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<string>('comments')
  const { id } = useParams()
  const {
    data: siteData,
    loading: siteInfoLoading,
    error: siteInfoError,
    refetch,
  } = useQuery(GET_TASK, {
    variables: {
      taskId: id,
    },
  })

  const siteInfo: ITask = siteData?.getTask

  let allVersions = siteInfo?.resource?.versions
  allVersions = allVersions?.slice()?.sort((v1, v2) => v1.version - v2.version)
  const latestVersion = allVersions?.[allVersions.length - 1]
  const [version, setVersion] = useState<number>(latestVersion?.version as number)
  useEffect(() => {
    setVersion(latestVersion?.version as number)
  }, [latestVersion])

  const versionDropdownItems = allVersions?.map((version) => {
    return {
      value: String(version.version),
      label: version.version,
    }
  })

  const currentResourceVersion = useMemo(() => {
    return allVersions?.find((val) => val.version === version)
  }, [allVersions, version])

  const { data, loading, error } = useQuery(DOWNLOAD_RESOURCE, {
    variables: {
      input: [{ resourceId: siteInfo?.resource?.id, version: version }],
    },
    skip: !isDownloading,
  })

  const downloadedResourceResp: Array<IDownloadResourceResp> = data?.downloadResource

  useEffect(() => {
    downloadResourceFromUri({
      downloadedResourceResp,
      isResourceDownloading: isDownloading,
      setIsResourceDownloading: setIsDownloading,
    })
  }, [downloadedResourceResp, isDownloading])

  const { data: assignmentData, loading: assigneeGroupsDataLoading } = useQuery(
    GET_ASSIGNEE_USER_GROUPS,
    {
      variables: {
        assignedToGroupId: siteInfo?.assignedToGroup?.id,
      },
      skip: !Boolean(siteInfo?.assignedToGroup?.id) && Boolean(siteInfo?.assignedToUser),
    }
  )

  const assigneeGroupsData: Array<IAssigneeGroup> = assignmentData?.getAllowedTaskAssignments

  const [assigneeGroupId, setAssigneeGroupId] = useState<string>()

  useEffect(() => {
    setAssigneeGroupId(assigneeGroupsData?.[0]?.group?.id)
  }, [assigneeGroupsData])

  const assigneeGroup = useMemo(() => {
    return assigneeGroupsData?.find((userGroup) => userGroup.group.id === assigneeGroupId)
  }, [assigneeGroupsData, assigneeGroupId])

  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(null)

  const assigneeUser = useMemo(() => {
    return assigneeGroup?.users?.find((user) => user.id === assigneeUserId)
  }, [assigneeGroup, assigneeUserId])

  const assigneeGroupDropdownItems = useMemo(() => {
    return assigneeGroupsData?.map((userGroup) => {
      return {
        value: userGroup?.group?.id,
        label: userGroup?.group?.name,
      }
    })
  }, [assigneeGroupsData])

  const assigneeUserDropdownItems = useMemo(() => {
    const allUsers = assigneeGroup?.users?.map((user) => {
      return {
        value: user?.id,
        label: user?.profile?.email,
      }
    })

    return allUsers ? [noneDropdownItem, ...allUsers] : [noneDropdownItem]
  }, [assigneeGroup])

  useEffect(() => {
    setAssigneeUserId(assigneeUserDropdownItems?.[0]?.value)
  }, [assigneeUserDropdownItems])

  const [assignTaskToUser, response] = useMutation(ASSIGN_TASKS_TO_USER)

  const handleAssign = () => {
    setIsAssigning(true)
    assignTaskToUser({
      variables: {
        input: [
          {
            taskId: siteInfo?.id,
            assignToUserId: assigneeUserId ?? undefined,
            assignToGroupId: assigneeGroupId,
          },
        ],
      },
      onCompleted: () => {
        message.success('Assignment successful')
      },
      onError: () => {
        message.error('Assignment failed')
      },
    })
      .then(() => setIsAssigning(false))
      .catch(() => setIsAssigning(false))
  }

  useEffect(() => {
    if (!isAssigning) {
      refetch()
    }
  }, [isAssigning, refetch])

  return (
    <>
      <Header>
        <Space
          direction="horizontal"
          size={'middle'}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(-1)}>
          <Typography.Link>
            <LeftOutlined style={{ verticalAlign: 'middle' }} />
          </Typography.Link>
          <div className="header-title">Site Details</div>
        </Space>
      </Header>
      {siteInfoLoading || !Boolean(version) ? (
        <Row justify={'center'} align={'middle'} style={{ width: '100%', padding: '0px 24px' }}>
          <Skeleton.Button active style={{ height: 500 }} block />
        </Row>
      ) : (
        <Row style={{ padding: '0px 24px' }} align={'top'}>
          <Col span={16} style={{ paddingRight: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size={'large'}>
              <Space direction="horizontal" align="center">
                <Typography.Text strong>Version: </Typography.Text>

                <Select
                  defaultValue={String(version)}
                  style={{ width: 'fit-content' }}
                  onChange={(value) => setVersion(Number(value))}
                  options={versionDropdownItems ?? []}
                  value={String(version)}
                />
              </Space>
              <Card style={{ width: '100%' }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                  }}>
                  <div className="site-details">
                    <Typography.Text>Provided ID: </Typography.Text>
                    <Typography.Text className="detail-value">
                      {siteInfo?.resource.resourceKey}
                    </Typography.Text>
                  </div>
                  <div className="site-details">
                    <Typography.Text>Created At: </Typography.Text>
                    <Typography.Text className="detail-value">
                      {currentResourceVersion?.createdAt != null
                        ? format(
                            new Date(currentResourceVersion?.createdAt),
                            COMMON_CONSTANTS.dateFormat
                          )
                        : '-'}
                    </Typography.Text>
                  </div>
                  <div className="site-details">
                    <Typography.Text>Updated At: </Typography.Text>
                    <Typography.Text className="detail-value">
                      {currentResourceVersion?.updatedAt != null
                        ? format(
                            new Date(currentResourceVersion?.updatedAt),
                            COMMON_CONSTANTS.dateFormat
                          )
                        : '-'}
                    </Typography.Text>
                  </div>
                  <div className="site-details">
                    <Typography.Text>Latest Uploaded Version:</Typography.Text>
                    <Typography.Text className="detail-value">
                      {latestVersion?.version}
                    </Typography.Text>
                  </div>
                  {currentResourceVersion?.createdBy?.profile?.email && (
                    <div className="site-details">
                      <Typography.Text>Created By: </Typography.Text>
                      <Typography.Text className="detail-value">
                        {currentResourceVersion?.createdBy?.profile?.email}
                      </Typography.Text>
                    </div>
                  )}
                </div>
              </Card>
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  setIsDownloading(true)
                }}
                type={isDownloading ? 'default' : 'primary'}
                title="Download Site">
                {isDownloading ? (
                  <Space direction="horizontal">
                    <Spin style={{ verticalAlign: 'middle' }} />
                    <Typography.Text>Downloading...</Typography.Text>
                  </Space>
                ) : (
                  <Space direction="horizontal">
                    <DownloadOutlined />
                    Download
                  </Space>
                )}
              </Button>
              {assigneeGroupsDataLoading ? (
                <Skeleton.Button active style={{ width: 700 }} />
              ) : (
                siteInfo?.assignedToGroup &&
                assigneeGroupDropdownItems?.length > 0 && (
                  <Space direction="horizontal" size={'middle'} style={{ marginBottom: 12 }}>
                    <Typography.Text strong>
                      Select user group and the user to assign site to:
                    </Typography.Text>

                    <Select
                      defaultValue={assigneeGroupId}
                      style={{ width: 'fit-content', minWidth: 120 }}
                      onChange={(value) => setAssigneeGroupId(value)}
                      options={assigneeGroupDropdownItems ?? []}
                      value={assigneeGroupId}
                    />
                    {(assigneeUserDropdownItems ?? [])?.length > 0 && (
                      <Select
                        defaultValue={assigneeUserId}
                        style={{ width: 'fit-content', minWidth: 150 }}
                        onChange={(value) => setAssigneeUserId(value)}
                        options={assigneeUserDropdownItems ?? []}
                        value={assigneeUserId}
                      />
                    )}
                    <Button type={isAssigning ? 'default' : 'primary'} onClick={handleAssign}>
                      {isAssigning ? (
                        <Space direction="horizontal" size={'middle'} align="center">
                          <Spin style={{ verticalAlign: 'middle' }} />
                          <Typography.Text>Assigning...</Typography.Text>
                        </Space>
                      ) : (
                        'Assign'
                      )}
                    </Button>
                  </Space>
                )
              )}
              <Tabs
                defaultActiveKey={activeTab}
                onChange={(activeKey) => setActiveTab(activeKey)}
                type="card"
                items={[
                  {
                    label: `Comments`,
                    key: 'comments',
                    children:
                      currentResourceVersion?.comments !== null ? (
                        <Typography.Text>
                          {currentResourceVersion?.comments === ''
                            ? 'No Comments'
                            : currentResourceVersion?.comments}
                        </Typography.Text>
                      ) : (
                        <Typography.Text>No Comments</Typography.Text>
                      ),
                  },
                ]}
              />
            </Space>
          </Col>
          <Col span={8} style={{ marginTop: 57 }}>
            <Collapse defaultActiveKey={['1']}>
              <Panel header="Stage Overview" key="1">
                {currentResourceVersion?.pipelineStatus && (
                  <Col style={{ padding: 12 }}>
                    <Status showTitle={false} fileStatus={currentResourceVersion?.pipelineStatus} />
                  </Col>
                )}
              </Panel>
            </Collapse>
          </Col>
        </Row>
      )}
    </>
  )
}

export default SiteDetails
